import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { Map, TrendingUp, TrendingDown } from '@mui/icons-material';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from 'recharts';

interface StateData {
  state: string;
  avgDelay: number;
  prevalence: number;
  accessScore: number;
  trend: 'up' | 'down';
}

const GeographicAnalysis: React.FC = () => {
  const [selectedCondition, setSelectedCondition] = useState('fibromyalgia');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [tooltipContent, setTooltipContent] = useState('');

  // Base state data with condition-specific multipliers
  const baseStateData: StateData[] = [
    { state: 'California', avgDelay: 487, prevalence: 3.2, accessScore: 78, trend: 'down' },
    { state: 'Texas', avgDelay: 612, prevalence: 2.8, accessScore: 65, trend: 'up' },
    { state: 'New York', avgDelay: 523, prevalence: 3.5, accessScore: 82, trend: 'down' },
    { state: 'Florida', avgDelay: 578, prevalence: 3.1, accessScore: 71, trend: 'up' },
    { state: 'Illinois', avgDelay: 541, prevalence: 2.9, accessScore: 74, trend: 'down' },
    { state: 'Pennsylvania', avgDelay: 595, prevalence: 3.3, accessScore: 69, trend: 'up' },
    { state: 'Ohio', avgDelay: 623, prevalence: 3.0, accessScore: 67, trend: 'up' },
    { state: 'Georgia', avgDelay: 601, prevalence: 2.7, accessScore: 68, trend: 'up' },
    { state: 'North Carolina', avgDelay: 589, prevalence: 2.9, accessScore: 70, trend: 'up' },
    { state: 'Michigan', avgDelay: 598, prevalence: 3.1, accessScore: 69, trend: 'up' },
    { state: 'New Jersey', avgDelay: 534, prevalence: 3.4, accessScore: 76, trend: 'down' },
    { state: 'Virginia', avgDelay: 556, prevalence: 3.0, accessScore: 73, trend: 'down' },
    { state: 'Washington', avgDelay: 502, prevalence: 3.3, accessScore: 77, trend: 'down' },
    { state: 'Arizona', avgDelay: 587, prevalence: 2.8, accessScore: 68, trend: 'up' },
    { state: 'Massachusetts', avgDelay: 518, prevalence: 3.6, accessScore: 81, trend: 'down' },
  ];

  // Region mapping for states
  const stateToRegion: { [key: string]: string } = {
    'California': 'west',
    'Washington': 'west',
    'Arizona': 'southwest',
    'Texas': 'southwest',
    'New York': 'northeast',
    'Massachusetts': 'northeast',
    'New Jersey': 'northeast',
    'Pennsylvania': 'northeast',
    'Florida': 'southeast',
    'Georgia': 'southeast',
    'North Carolina': 'southeast',
    'Virginia': 'southeast',
    'Illinois': 'midwest',
    'Ohio': 'midwest',
    'Michigan': 'midwest',
  };

  // Condition-specific multipliers for delays and prevalence
  const conditionMultipliers: { [key: string]: { delayMult: number; prevMult: number } } = {
    fibromyalgia: { delayMult: 1.0, prevMult: 1.0 },
    cfs: { delayMult: 1.15, prevMult: 0.8 },
    endometriosis: { delayMult: 1.25, prevMult: 0.6 },
    autoimmune: { delayMult: 0.95, prevMult: 1.2 },
    pots: { delayMult: 1.3, prevMult: 0.5 },
  };

  // Calculate dynamic state data based on selected condition
  const getStateData = (): StateData[] => {
    const multipliers = conditionMultipliers[selectedCondition];
    return baseStateData.map(state => ({
      ...state,
      avgDelay: Math.round(state.avgDelay * multipliers.delayMult),
      prevalence: parseFloat((state.prevalence * multipliers.prevMult).toFixed(1)),
    }));
  };

  // Filter states by region
  const getFilteredStateData = (): StateData[] => {
    const data = getStateData();
    if (selectedRegion === 'all') return data;
    return data.filter(state => stateToRegion[state.state] === selectedRegion);
  };

  const stateData = getFilteredStateData();

  // Calculate regional data dynamically
  const getRegionalData = () => {
    const allData = getStateData();
    const regions = ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West'];
    
    return regions.map(region => {
      const regionKey = region.toLowerCase();
      const regionStates = allData.filter(state => stateToRegion[state.state] === regionKey);
      
      if (regionStates.length === 0) {
        return { region, avgDelay: 0, prevalence: 0, accessScore: 0 };
      }

      const avgDelay = Math.round(
        regionStates.reduce((sum, s) => sum + s.avgDelay, 0) / regionStates.length
      );
      const prevalence = parseFloat(
        (regionStates.reduce((sum, s) => sum + s.prevalence, 0) / regionStates.length).toFixed(1)
      );
      const accessScore = Math.round(
        regionStates.reduce((sum, s) => sum + s.accessScore, 0) / regionStates.length
      );

      return { region, avgDelay, prevalence, accessScore };
    });
  };

  const regionalData = getRegionalData();

  // Calculate national statistics
  const getNationalStats = () => {
    const data = getStateData();
    const avgDelay = Math.round(data.reduce((sum, s) => sum + s.avgDelay, 0) / data.length);
    const bestState = data.reduce((min, s) => s.avgDelay < min.avgDelay ? s : min);
    const worstState = data.reduce((max, s) => s.avgDelay > max.avgDelay ? s : max);
    
    return { avgDelay, bestState, worstState };
  };

  const nationalStats = getNationalStats();

  // Get condition name for display
  const getConditionName = () => {
    const names: { [key: string]: string } = {
      fibromyalgia: 'Fibromyalgia',
      cfs: 'Chronic Fatigue Syndrome',
      endometriosis: 'Endometriosis',
      autoimmune: 'Autoimmune Disorders',
      pots: 'POTS',
    };
    return names[selectedCondition];
  };

  const getAccessScoreColor = (score: number) => {
    if (score >= 75) return '#2e7d32';
    if (score >= 60) return '#ed6c02';
    return '#dc004e';
  };

  const getDelayColor = (delay: number) => {
    if (delay < 520) return '#2e7d32';
    if (delay < 580) return '#ed6c02';
    return '#dc004e';
  };

  // GeoJSON URL for US states
  const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <Map sx={{ mr: 2, verticalAlign: 'middle' }} />
          Geographic Analysis
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          State-by-state analysis of diagnostic delays, prevalence, and healthcare access
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select
                value={selectedCondition}
                onChange={(e) => setSelectedCondition(e.target.value)}
                label="Condition"
              >
                <MenuItem value="fibromyalgia">Fibromyalgia</MenuItem>
                <MenuItem value="cfs">Chronic Fatigue Syndrome</MenuItem>
                <MenuItem value="endometriosis">Endometriosis</MenuItem>
                <MenuItem value="autoimmune">Autoimmune Disorders</MenuItem>
                <MenuItem value="pots">POTS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Region</InputLabel>
              <Select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                label="Region"
              >
                <MenuItem value="all">All Regions</MenuItem>
                <MenuItem value="northeast">Northeast</MenuItem>
                <MenuItem value="southeast">Southeast</MenuItem>
                <MenuItem value="midwest">Midwest</MenuItem>
                <MenuItem value="southwest">Southwest</MenuItem>
                <MenuItem value="west">West</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Map Visualization */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={3} sx={{ p: 3, height: '500px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              US Diagnostic Delay Heat Map
            </Typography>
            <Box sx={{ mt: 2, height: '380px' }}>
              <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: '100%' }}>
                <ZoomableGroup>
                  <Geographies geography={geoUrl}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const stateName = geo.properties.name;
                        const stateInfo = stateData.find(s => s.state === stateName);
                        const delay = stateInfo?.avgDelay || 550;
                        
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={getDelayColor(delay)}
                            stroke="#FFFFFF"
                            strokeWidth={0.5}
                            onMouseEnter={() => {
                              setTooltipContent(`${stateName}: ${delay} days`);
                            }}
                            onMouseLeave={() => {
                              setTooltipContent('');
                            }}
                            style={{
                              default: { outline: 'none' },
                              hover: { fill: '#1976d2', outline: 'none', cursor: 'pointer' },
                              pressed: { outline: 'none' },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
              {tooltipContent && (
                <Box
                  sx={{
                    position: 'absolute',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: 1,
                    fontSize: '14px',
                    pointerEvents: 'none',
                    mt: -5,
                    ml: 2,
                  }}
                >
                  {tooltipContent}
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: '#2e7d32', borderRadius: 1 }} />
                  <Typography variant="caption">{'< 520 days'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: '#ed6c02', borderRadius: 1 }} />
                  <Typography variant="caption">520-580 days</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: '#dc004e', borderRadius: 1 }} />
                  <Typography variant="caption">{'> 580 days'}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Key Metrics */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {selectedRegion === 'all' ? 'National' : selectedRegion.charAt(0).toUpperCase() + selectedRegion.slice(1)} Average Delay
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="error">
                    {nationalStats.avgDelay} days
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {Math.round(nationalStats.avgDelay / 30)} months average for {getConditionName()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Best Performing State
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                    {nationalStats.bestState.state}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {nationalStats.bestState.avgDelay} days average
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Needs Improvement
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                    {nationalStats.worstState.state}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {nationalStats.worstState.avgDelay} days average
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Regional Comparison Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Regional Comparison
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={regionalData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgDelay" fill="#dc004e" name="Avg Delay (days)" />
                <Bar dataKey="accessScore" fill="#1976d2" name="Access Score" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Scatter Plot: Delay vs Access Score */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Delay vs Healthcare Access
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="accessScore" 
                  name="Access Score"
                  label={{ value: 'Healthcare Access Score', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="avgDelay" 
                  name="Avg Delay"
                  label={{ value: 'Avg Delay (days)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter name="States" data={stateData} fill="#1976d2" />
              </ScatterChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* State Data Table */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              State-by-State Comparison
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell align="right"><strong>Avg. Delay (days)</strong></TableCell>
                    <TableCell align="right"><strong>Prevalence (%)</strong></TableCell>
                    <TableCell align="right"><strong>Access Score</strong></TableCell>
                    <TableCell align="center"><strong>Trend</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stateData.map((row) => (
                    <TableRow key={row.state} hover>
                      <TableCell component="th" scope="row">
                        <strong>{row.state}</strong>
                      </TableCell>
                      <TableCell align="right">{row.avgDelay}</TableCell>
                      <TableCell align="right">{row.prevalence}%</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.accessScore}
                          size="small"
                          sx={{
                            backgroundColor: getAccessScoreColor(row.accessScore),
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.trend === 'up' ? (
                          <MuiTooltip title="Delays increasing">
                            <TrendingUp color="error" />
                          </MuiTooltip>
                        ) : (
                          <MuiTooltip title="Delays decreasing">
                            <TrendingDown color="success" />
                          </MuiTooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Regional Insights */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Regional Insights
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Urban vs Rural Disparity
                  </Typography>
                  <Typography variant="body2">
                    Rural areas experience 45% longer diagnostic delays due to limited specialist access
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, backgroundColor: '#fce4ec', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Insurance Impact
                  </Typography>
                  <Typography variant="body2">
                    Uninsured patients wait 2.3x longer for diagnosis compared to privately insured
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GeographicAnalysis;

// Made with Bob
